// Copyright (c) 2022 by Move Closer

import { AnyObject, IResponse, mapModel, ResourceActionFailed } from '@movecloser/front-core'

import {
  AllowedAttributes,
  CartData,
  CartIdentifier,
  CartItemData,
  IGraphQL,
  ProductData,
  ProductSneakPeakData
} from '../../../contexts'
import { logger, resolveFromStatus } from '../../../support'

import { IProductsRepository } from '../../products/contracts/repositories'

import { Cart } from '../models/cart'
import { cartAdapterMap } from '../models/cart.adapter'
import { CartModel } from '../contracts/models'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export abstract class CartAwareAbstract {
  constructor (
    protected readonly connector: IGraphQL,
    protected readonly products: IProductsRepository
  ) {
  }

  /**
   * Load missing products' definitions & compose Cart model.
   * @param candidate
   * @param giftFromAttribute (specifies whether the 'isGift' property should be resolved from product attributes
   */
  protected async composeCart (candidate: CartData, giftFromAttribute: boolean = false): Promise<CartModel> {
    candidate.items = candidate.items.filter(item => item !== null)

    const mappedCandidate: CartData = mapModel(candidate, cartAdapterMap, false)

    if (mappedCandidate.items.length > 0) {
      const items: CartItemData[] = []

      try {
        // Note! This is required because of mapping by merge.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const skuCollection: string[] = mappedCandidate.items.map(i => i.product.sku)
        const productsCollection = await this.products.loadProductsBySkus(skuCollection)

        const productsMap: Record<string, ProductSneakPeakData> = {}

        for (const product of productsCollection) {
          const variants = Object.values(product.variants)
          const variantsKeys = Object.keys(product.variants)

          for (let i = 0; i < variants.length; i++) {
            for (let j = 0; j < skuCollection.length; j++) {
              if (variants[i].sku === skuCollection[j]) {
                const fVariant = variants[i]
                productsMap[variants[i].sku] = {
                  ...product,
                  variants: { [variantsKeys[i]]: fVariant }
                }
                break
              }
            }
          }
        }

        for (const i of mappedCandidate.items) {
          let isGift = false
          let isSample = false
          try {
            isGift = await this.detectedIfGift(i, giftFromAttribute)
            isSample = await this.detectIfSample(i)
          } catch (e) {
            logger(e, 'warn')
          }

          items.push({
            ...i,
            isGift: isGift,
            isSample: isSample,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            product: productsMap[i.product.sku] ?? null
          })
        }

        mappedCandidate.items = items
      } catch (e) {
        throw new ResourceActionFailed(
          'Failed to resolve Products',
          resolveFromStatus(500)
        )
      }
    }

    return Cart.hydrate<CartModel>(mappedCandidate)
  }

  /**
   * !!!! TODO !!!!
   *
   * This method originally should NOT be a part of abstract class because it applies specific
   * type of resolving isGift property, applicable only for specific case (here Lirene)!
   */
  protected async detectedIfGift (item: AnyObject, giftFromAttribute: boolean): Promise<boolean> {
    if (giftFromAttribute) {
      const keywords = ['gratis', 'free']

      const toTest: string = item.product.sku.toLowerCase()

      for (const k of keywords) {
        const shorten: string = toTest.replace(k, '')
        if (shorten.length < toTest.length) {
          return true
        }
      }

      return false
    } else {
      try {
        const product: Array<ProductData> = await this.products.loadProductsBySkus([item.product.sku])
        const candidate = product.length ? Object.values(product[0].variants).find((product) => product.sku === item.product.sku) : undefined

        if (candidate && candidate.attributes[AllowedAttributes.IsGift]) {
          return candidate.attributes[AllowedAttributes.IsGift] as boolean
        } else {
          return false
        }
      } catch (e) {
        console.error(e)
      }
      return false
    }
  }

  /**
   * !!!! TODO !!!!
   *
   * This method originally should NOT be a part of abstract class because it applies specific
   * type of resolving isGift property, applicable only for specific case (here Lirene)!
   */
  protected async detectIfSample (item: AnyObject): Promise<boolean> {
    try {
      const product: Array<ProductData> = await this.products.loadProductsBySkus([item.product.sku])

      if (product && product.length && Object.values(product[0].variants)[0].attributes[AllowedAttributes.IsSample]) {
        return Object.values(product[0].variants)[0].attributes[AllowedAttributes.IsSample] as boolean
      } else {
        return false
      }
    } catch (e) {
      console.error(e)
    }
    return false
  }

  /**
   * Loads the cart by its id.
   */
  protected async loadCartById (id: CartIdentifier): Promise<IResponse> {
    return await this.connector.call('cartQuery', { cartId: id })
  }
}
